import { useState, useEffect } from 'react';
import { Bot, Play, CheckCircle, XCircle, Loader2, Trash2, MessageSquare, AlertCircle, ExternalLink, Sparkles, Terminal } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';

// Import the REAL components used in Dashboard
import { LogEntryItem } from '../../components/cars/LogEntryItem';
import { NoteItem } from '../../components/cars/NoteItem';
import { SparePartItem } from '../../components/cars/SparePartItem';
import { LocationItem } from '../../components/cars/LocationItem';
import { ReminderItem } from '../../components/cars/ReminderItem';

interface JarvisTest {
    id: string;
    name: string;
    prompt: string;
    expectedText: string;
    expectedActions: string[];
    status: 'pending' | 'running' | 'pass' | 'fail';
    resultText?: string;
    foundActions?: string[];
    dbRecords?: any[];
    dbVerification?: {
        logs: number;
        parts: number;
        locations: number;
        reminders: number;
    };
    error?: string;
    debugLogs?: string[];
}

const TEST_CAR_SLUG = 'ax7ehdl1tn';

export function AdminJarvisTests() {
    const { user } = useAuth();
    const { preferences } = usePreferences();
    const [carId, setCarId] = useState<string | null>(null);
    const [isTesting, setIsTesting] = useState(false);
    const [tests, setTests] = useState<JarvisTest[]>([
        {
            id: 'multi-part-receipt',
            name: 'Complex Receipt Parsing (Oil + Filters)',
            prompt: 'izveido jaunu izdevumu ierakstu - biju auto rezerves daļu veikalā - rīgā, lugažu ielā 5, šodien, un nopirku eļļu 5w40 pa 20 eur, eļļas filtru pa 2.5 eur un gaisa filtru 10 eur. Gaisa filtram kods ir 525244 eļļas filtram kods ir 112555 un firma kahl un gasa filtram firma ir brembo un eļļa ir liqui molly',
            expectedText: '32.50',
            expectedActions: ['upsert_log'],
            status: 'pending',
            debugLogs: []
        },
        {
            id: 'complex-diagnostic-golf-tpms',
            name: 'Complex Diagnosis (Golf Mk7 TPMS + Noise)',
            prompt: "man golf mk7 tdi 2.0 2014 g auto 2 problēmas šodien braucot pa šoseju. Sāka kautkas čirkstēt - liekas no aizmugures, it kā piebremzējot un atkal braucot - pārstāja bet šad tad parādījās. Tad iebraucu uzpildes stacijā piebraukt uzpildīt riepas - lēni ripojot tad gan bija skaļāk un ilgāk čīīk čīīk čīīk tāda čīkstoņa, bet uz brīdi nedaudz piespiežot bremžu pedāli - tas neko neizmainīja. Apskatot riteņus, bremžu aizsargus, it kā neko neredzu, viss stingri, pēc tam sākot braukt nekas nečīkstēja bet pa ceļam šad tad vēl uz brīdi iečirkstējās. Domāju, varbūt kāds akmentiņš iestrēdzis tā, ka nav izkritis bet šad tad kautkā tomēr pieskaras? Un otrā lieta - man riepas ir uzpumpētas uz 2.9 atmosfērām. pirms 2 dienām braucot- auto saka ka visas riepas vajag pārbaudīt (man ir tā gaisa spiediena kontrole uz vibrāciju laikam, kas nesaka precīzi spiedienu bet tikai - ka kautkas mainās?) un galā tiekot - uzpildes stacijā, visu pārbaudu, viss tad ir ideāli uz 2.9 un resetoju. Tad šodien braucu atkal pa šoseju no ventspils uz Rīgu - un sākumā jau izmetas paziņojums - ka aizmugurē labā pusē vajag pārbaudīt spiedienu. Tad tiku uz to uzpildes staciju - pārbaudu, nu nekas nav izmainījies. Tad resetoju. Tieku gandrīz līdz Rīgai un atkal izmetas atkal par to pašu riepu, atkal aizbraucu pārbaudīt - bez izmaiņām Viss kārtībā. Resetoju atkal tad to iestatījumu. Tad jautājums - vai pirmā gadījumā tas izklausās pēc akmentiņa, vai kautkas cits vēl var būt? un otrā gadījumā - vai kautkāds sensors, labā aizmugurējā pusē defektīvs (vai tas sensors ir tas kas čīkst?) vai arī viņam nepatīk ka riepas ir 2.9 nevis 2.6 uzpumpetas?",
            expectedText: "ABS",
            expectedActions: [],
            status: 'pending',
            debugLogs: []
        }
    ]);

    // Construct effective preferences for LogEntryItem
    const effectivePreferences = preferences ? {
        ...preferences,
        distance_unit: preferences.distance_unit || 'km',
        volume_unit: preferences.volume_unit || 'liters',
        currency: preferences.currency || 'EUR',
        currency_symbol: preferences.currency_symbol || '€'
    } : null;

    useEffect(() => {
        const fetchCarId = async () => {
            const { data } = await supabase.from('cars').select('id').eq('slug', TEST_CAR_SLUG).single();
            if (data) setCarId(data.id);
        };
        fetchCarId();
    }, []);

    const addDebugLog = (testId: string, log: string) => {
        setTests(prev => prev.map(t => t.id === testId ? { ...t, debugLogs: [...(t.debugLogs || []), log] } : t));
        console.log(`[JARVIS-TEST] ${log}`);
    };

    const clearTempData = async (silent = false) => {
        if (!carId) return;
        if (!silent) {
            const confirm = window.confirm(`Vēlaties dzēst visus pagaidu (temporary) ierakstus šim auto (${TEST_CAR_SLUG})?`);
            if (!confirm) return;
        }

        try {
            const { data: logsToDelete } = await supabase.from('log_entries').select('id').eq('car_id', carId).eq('is_temporary', true);
            const logIds = logsToDelete?.map(l => l.id) || [];

            if (logIds.length > 0) {
                await supabase.from('log_entry_parts').delete().in('log_entry_id', logIds);
                await supabase.from('expenses').delete().in('log_entry_id', logIds);
                await supabase.from('log_entries').delete().in('id', logIds);
            }
            
            await supabase.from('spare_parts').delete().eq('car_id', carId).eq('is_temporary', true);
            await supabase.from('notes').delete().eq('car_id', carId).eq('is_temporary', true);
            await supabase.from('reminders').delete().eq('car_id', carId).eq('is_temporary', true);
            
            if (!silent) alert('Pagaidu dati izdzēsti.');
            setTests(prev => prev.map(t => ({ ...t, dbRecords: [], dbVerification: { logs: 0, parts: 0, locations: 0, reminders: 0 } })));
        } catch (e) {
            console.error(e);
            if (!silent) alert('Kļūda dzēšot datus.');
        }
    };

    const executeAction = async (testId: string, action: any) => {
        if (!carId) return null;
        const { type, data } = action;
        const is_temporary = action.is_temporary ?? data?.is_temporary ?? true;

        addDebugLog(testId, `Executing ${type} action...`);

        if (type === 'upsert_log') {
            const typeId = typeof data.type_id === 'string' ? parseInt(data.type_id.replace(/\D/g, '')) : (data.type_id || 5);
            
            let locationId = null;
            if (data.location_name) {
                const { data: locs } = await supabase.from('locations').select('id, name').eq('user_id', user.id);
                const match = locs?.find(l => l.name.toLowerCase().includes(data.location_name.toLowerCase()));
                if (match) locationId = match.id;
                else {
                    const { data: newLoc } = await supabase.from('locations').insert({ user_id: user.id, name: data.location_name, is_temporary: is_temporary }).select().single();
                    if (newLoc) locationId = newLoc.id;
                }
            }

            const { data: entry, error: logError } = await supabase.from('log_entries').insert({
                car_id: carId,
                user_id: user.id,
                date: data.date || new Date().toISOString().split('T')[0],
                location_id: locationId,
                mileage: data.odometer || data.mileage,
                car_value: data.car_value,
                is_temporary: is_temporary
            }).select().single();

            if (logError) {
                addDebugLog(testId, `DB ERROR (log): ${logError.message}`);
                throw logError;
            }

            // 2. Create Expense Record (Detail)
            const typeToKey: any = { 1: 'fuel', 2: 'car_wash', 3: 'repairs', 4: 'maintenance', 5: 'parts', 6: 'insurance', 7: 'inspection', 8: 'tyres', 9: 'parking', 10: 'electricity', 11: 'taxes', 12: 'other', 13: 'info', 14: 'tolls' };
            const { error: expError } = await supabase.from('expenses').insert({
                log_entry_id: entry.id,
                user_id: user.id,
                car_id: carId,
                date: entry.date,
                type: typeToKey[typeId] || 'other',
                amount: data.total_amount || data.price || 0,
                title: ''
            });

            if (expError) {
                addDebugLog(testId, `DB ERROR (expense): ${expError.message}`);
                throw expError;
            }

            if (entry && data.parts) {
                addDebugLog(testId, `Linking ${data.parts.length} parts...`);
                for (const part of data.parts) {
                    addDebugLog(testId, `Processing part: ${part.name} (${part.manufacturer || 'no mfr'})`);
                    let partId;
                    const { data: existing, error: lookupErr } = await supabase.from('spare_parts')
                        .select('id')
                        .eq('user_id', user.id)
                        .eq('car_id', carId)
                        .eq('name', part.name)
                        .maybeSingle();

                    if (lookupErr) addDebugLog(testId, `Lookup Error: ${lookupErr.message}`);

                    if (existing) {
                        addDebugLog(testId, `Found existing part: ${existing.id}`);
                        partId = existing.id;
                    } else {
                        addDebugLog(testId, `Creating new part...`);
                        const { data: newPart, error: insertPartErr } = await supabase.from('spare_parts').insert({
                            user_id: user.id,
                            car_id: carId,
                            name: part.name,
                            manufacturer: part.manufacturer,
                            part_code: part.part_code || part.part_number,
                            is_temporary: is_temporary
                        }).select().single();
                        
                        if (insertPartErr) addDebugLog(testId, `Insert Part Error: ${insertPartErr.message}`);
                        partId = newPart?.id;
                        if (partId) addDebugLog(testId, `Created part: ${partId}`);
                    }

                    if (partId) {
                        addDebugLog(testId, `Linking part ${partId} to log ${entry.id}...`);
                        const { error: linkErr } = await supabase.from('log_entry_parts').insert({
                            log_entry_id: entry.id,
                            spare_part_id: partId,
                            quantity: part.quantity || 1,
                            price_per_unit: part.price_per_unit || part.price || part.amount || 0,
                            purchased_at: entry.date
                        });
                        if (linkErr) addDebugLog(testId, `Link Error: ${linkErr.message}`);
                        else addDebugLog(testId, `Part linked successfully.`);
                    }
                }
            }

            addDebugLog(testId, `Log created successfully: ${entry.id}`);

            // Fetch enriched
            const { data: fullEntry } = await supabase
                .from('log_entries')
                .select(`*, expenses (*), location:locations (*), log_entry_parts(spare_part_id, spare_parts(name, manufacturer))`)
                .eq('id', entry.id)
                .single();

            return { ...fullEntry, type: 'log', car_slug: TEST_CAR_SLUG };
        }

        if (type === 'upsert_note') {
            const { data: note, error } = await supabase.from('notes').insert({
                car_id: carId,
                user_id: user.id,
                content: data.content,
                is_temporary: is_temporary
            }).select().single();
            if (error) throw error;
            return { ...note, type: 'note' };
        }

        if (type === 'upsert_part') {
            const { data: part, error } = await supabase.from('spare_parts').insert({
                car_id: carId,
                user_id: user.id,
                name: data.name,
                manufacturer: data.manufacturer,
                part_code: data.part_code || data.part_number,
                price: data.price,
                is_temporary: is_temporary
            }).select().single();
            if (error) throw error;
            return { ...part, type: 'part' };
        }

        if (type === 'upsert_location') {
            const { data: loc, error } = await supabase.from('locations').insert({
                user_id: user.id,
                name: data.name,
                address: data.address,
                is_temporary: is_temporary
            }).select().single();
            if (error) throw error;
            return { ...loc, type: 'location' };
        }

        if (type === 'upsert_reminder') {
            const { data: rem, error } = await supabase.from('reminders').insert({
                car_id: carId,
                user_id: user.id,
                name: data.name,
                description: data.description,
                remind_date: data.remind_date,
                is_active: true,
                is_temporary: is_temporary
            }).select().single();
            if (error) throw error;
            return { ...rem, type: 'reminder' };
        }

        return null;
    };

    const runTest = async (testId: string) => {
        const test = tests.find(t => t.id === testId);
        if (!test || !carId) return;

        setIsTesting(true);
        // Clear previous results and logs
        setTests(prev => prev.map(t => t.id === testId ? { ...t, status: 'running', error: undefined, dbRecords: [], debugLogs: [] } : t));
        
        // Auto-clear DB for this car before test
        await clearTempData(true);
        addDebugLog(testId, "Starting fresh test...");

        try {
            const { data: { session } } = await supabase.auth.getSession();
            addDebugLog(testId, "Calling Jarvis worker...");
            
            const response = await fetch("https://mycarcosts-jarvis.gtreulands.workers.dev/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token}` },
                body: JSON.stringify({ message: test.prompt, carId: carId, isTest: true, includeData: true, userRole: 'Super Admin' })
            });

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let accumulatedContent = "";
            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    accumulatedContent += decoder.decode(value, { stream: true });
                }
            }

            const [textPart, dataPart] = accumulatedContent.split('|---DATA---|');
            addDebugLog(testId, `Worker returned. Data length: ${dataPart?.length || 0}`);
            
            // 1. Identify all balanced action tags
            const actionTags: string[] = [];
            let searchPos = 0;
            const dataStr = dataPart || "";
            while (true) {
                const actionStart = dataStr.indexOf('[ACTION:', searchPos);
                if (actionStart === -1) break;
                
                let bracketCount = 0;
                let actionEnd = -1;
                for (let i = actionStart; i < dataStr.length; i++) {
                    if (dataStr[i] === '[') bracketCount++;
                    if (dataStr[i] === ']') {
                        bracketCount--;
                        if (bracketCount === 0) {
                            actionEnd = i;
                            break;
                        }
                    }
                }
                if (actionEnd === -1) break;
                actionTags.push(dataStr.substring(actionStart, actionEnd + 1));
                searchPos = actionEnd + 1;
            }

            addDebugLog(testId, `Found ${actionTags.length} action tags.`);
            
            // 2. Execute each action
            const executedRecords = [];
            for (const actStr of actionTags) {
                try {
                    const jsonStr = actStr.replace(/\[ACTION:\s*/i, '').replace(/\]$/, '').trim();
                    const action = JSON.parse(jsonStr);
                    const rec = await executeAction(testId, action);
                    if (rec) {
                        executedRecords.push(rec);
                        setTests(prev => prev.map(t => t.id === testId ? { ...t, dbRecords: [...executedRecords] } : t));
                    }
                } catch (e: any) { 
                    addDebugLog(testId, `JSON Parse Error: ${e.message}`);
                }
            }

            const { data: logs } = await supabase.from('log_entries').select('id').eq('car_id', carId).eq('is_temporary', true);
            const { data: parts } = await supabase.from('spare_parts').select('id').eq('car_id', carId).eq('is_temporary', true);
            const { data: rems } = await supabase.from('reminders').select('id').eq('car_id', carId).eq('is_temporary', true);
            const { data: locs } = await supabase.from('locations').select('id').eq('user_id', user.id).eq('is_temporary', true);

            const textPass = textPart.includes(test.expectedText);
            const dbPass = (logs?.length || 0) > 0 || (rems?.length || 0) > 0;

            setTests(prev => prev.map(t => t.id === testId ? { 
                ...t, 
                status: (textPass && dbPass) ? 'pass' : 'fail',
                resultText: textPart.replace(/\[PROGRESS:[\s\S]*?\]/g, '').trim(),
                foundActions: actionTags,
                dbVerification: { 
                    logs: logs?.length || 0, 
                    parts: parts?.length || 0, 
                    locations: locs?.length || 0,
                    reminders: rems?.length || 0
                },
                error: !textPass ? 'Atbildes teksts neatbilst gaidītajam.' : undefined
            } : t));

        } catch (err: any) {
            addDebugLog(testId, `CRITICAL ERROR: ${err.message}`);
            setTests(prev => prev.map(t => t.id === testId ? { ...t, status: 'fail', error: err.message } : t));
        } finally {
            setIsTesting(false);
        }
    };

    const renderLink = (link: any) => {
        const slug = TEST_CAR_SLUG;
        const isTemp = link.is_temporary;

        const Content = () => {
            if (link.type === 'note') return <NoteItem note={link} slug={slug} compact={true} openInNewTab={true} />;
            if (link.type === 'part') return <SparePartItem part={link} slug={slug} compact={true} openInNewTab={true} />;
            if (link.type === 'location') return <LocationItem location={link} slug={slug} compact={true} />;
            return <LogEntryItem entry={link} slug={slug} effectivePreferences={effectivePreferences} openInNewTab={true} />;
        };

        return (
            <div key={link.id} className="relative bg-white dark:bg-white/5 rounded-2xl border border-slate-800 overflow-hidden shadow-2xl transition-all hover:border-purple-500/50 animate-in zoom-in duration-300">
                <Content />
                {isTemp && (
                    <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/10 flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-500">Pagaidu ieraksts</span>
                        <ExternalLink size={12} className="text-slate-500" />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8 font-primary text-slate-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-white flex items-center gap-3">
                        <Bot className="text-purple-500" />
                        Jarvis Test Lab
                    </h1>
                    <p className="text-slate-400 mt-1">Verify AI logic with real database inserts and visual cards.</p>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => clearTempData()}
                        className="flex items-center gap-2 bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400 px-4 py-3 rounded-2xl font-bold transition-all border border-slate-700"
                    >
                        <Trash2 size={18} />
                        Clear Temp Data
                    </button>
                    <button
                        disabled={isTesting}
                        onClick={() => tests.forEach(t => runTest(t.id))}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-purple-500/20 disabled:opacity-50"
                    >
                        {isTesting ? <Loader2 className="animate-spin" /> : <Play size={20} />}
                        Run All Tests
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {tests.map(test => (
                    <div key={test.id} className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden p-8 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {test.status === 'pass' && <CheckCircle className="text-green-500" />}
                                {test.status === 'fail' && <XCircle className="text-red-500" />}
                                {test.status === 'running' && <Loader2 className="animate-spin text-blue-500" />}
                                {test.status === 'pending' && <AlertCircle className="text-slate-600" />}
                                <h3 className="text-2xl font-black text-white uppercase tracking-tight">{test.name}</h3>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">User Input</label>
                                <div className="p-5 bg-black/50 rounded-2xl text-sm italic text-slate-300 border border-slate-800 leading-relaxed shadow-inner">
                                    "{test.prompt}"
                                </div>
                                
                                {/* Debug Log Window */}
                                <div className="space-y-2 mt-4">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                        <Terminal size={12} /> Execution Log
                                    </label>
                                    <div className="p-4 bg-black/80 rounded-2xl border border-slate-800 h-[150px] overflow-y-auto font-mono text-[10px] text-green-500/80 space-y-1">
                                        {test.debugLogs?.map((log, i) => (
                                            <div key={i}><span className="opacity-30">[{i}]</span> {log}</div>
                                        ))}
                                        {test.debugLogs?.length === 0 && <span className="opacity-20 italic">No logs yet...</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">AI Response Text</label>
                                <div className="p-5 bg-slate-800/30 rounded-2xl text-sm text-slate-100 border border-slate-700 min-h-[120px] leading-relaxed">
                                    {test.resultText || <span className="opacity-20 italic">Click Run to start...</span>}
                                </div>

                                {test.dbRecords && test.dbRecords.length > 0 && (
                                    <div className="space-y-4 mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        <label className="text-[10px] font-black text-purple-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                            <Sparkles size={14} /> Resulting UI Cards
                                        </label>
                                        <div className="space-y-4">
                                            {test.dbRecords.map((rec) => renderLink(rec))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-6 border-t border-slate-800">
                            <StatBox label="Logs Created" value={test.dbVerification?.logs} />
                            <StatBox label="Parts Linked" value={test.dbVerification?.parts} />
                            <StatBox label="Reminders" value={test.dbVerification?.reminders} />
                            <StatBox label="Locations" value={test.dbVerification?.locations} />
                            <StatBox label="AI Actions" value={test.foundActions?.length} unit="tags" />
                            <div className="flex items-center justify-center">
                                <button 
                                    disabled={isTesting}
                                    onClick={() => runTest(test.id)}
                                    className="w-full py-4 bg-slate-800 hover:bg-purple-600 text-white text-xs font-black rounded-2xl transition-all uppercase tracking-widest border border-slate-700 hover:border-purple-500/40 disabled:opacity-30 shadow-lg"
                                >
                                    {isTesting ? 'Testing...' : 'Run Test'}
                                </button>
                            </div>
                        </div>

                        {test.error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-xs font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                                <XCircle size={14} />
                                {test.error}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatBox({ label, value, unit }: { label: string; value?: number; unit?: string }) {
    return (
        <div className="p-5 bg-slate-800/40 rounded-2xl border border-slate-800">
            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{label}</div>
            <div className={`text-2xl font-black ${value ? 'text-white' : 'text-slate-700'}`}>
                {value || 0} <span className="text-[10px] opacity-40 ml-1">{unit}</span>
            </div>
        </div>
    );
}
